/**
 * API request utility function
 * @param url API endpoint URL
 * @param options Request options
 * @returns Promise with response data
 */
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  // Set default headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }
  // Add authentication token if available
  const token = localStorage.getItem('auth_token')
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  try {
    // Make the request
    const response = await fetch(url, {
      ...options,
      headers,
    })
    // Handle non-2xx responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'An unknown error occurred',
      }))
      throw new ApiError(
        errorData.message || 'Request failed',
        response.status,
        errorData,
      )
    }
    // Parse and return the response data
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return await response.json()
    } else {
      throw new Error('Unexpected response format')
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    // Handle network errors
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0,
    )
  }
}
/**
 * Custom API Error class
 */
export class ApiError extends Error {
  status: number
  data?: any
  constructor(message: string, status: number, data?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}
/**
 * Handle API errors
 * @param error Error object
 * @returns Formatted error message
 */
export function handleApiError(error: unknown): string {
  if (error instanceof ApiError) {
    // Handle specific status codes
    switch (error.status) {
      case 401:
        return 'Authentication required. Please log in again.'
      case 403:
        return 'You do not have permission to perform this action.'
      case 404:
        return 'The requested resource was not found.'
      case 422:
        return (
          error.data?.message || 'Validation failed. Please check your input.'
        )
      case 500:
        return 'Server error. Please try again later.'
      default:
        return error.message
    }
  }
  // Generic error handling
  return error instanceof Error ? error.message : 'An unknown error occurred'
}
